import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowRightLeft, DollarSign, Ruler, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currencies = ["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "CHF", "CNY", "INR", "MXN"];

const unitCategories = {
  length: {
    label: "Length",
    units: [
      { value: "m", label: "Meters (m)" },
      { value: "ft", label: "Feet (ft)" },
      { value: "cm", label: "Centimeters (cm)" },
      { value: "in", label: "Inches (in)" },
    ]
  },
  area: {
    label: "Area",
    units: [
      { value: "sqm", label: "Square Meters (m²)" },
      { value: "sqft", label: "Square Feet (ft²)" },
    ]
  },
  weight: {
    label: "Weight",
    units: [
      { value: "kg", label: "Kilograms (kg)" },
      { value: "lb", label: "Pounds (lb)" },
    ]
  },
  volume: {
    label: "Volume",
    units: [
      { value: "l", label: "Litres (L)" },
      { value: "gal", label: "Gallons (gal)" },
    ]
  },
  temperature: {
    label: "Temperature",
    units: [
      { value: "c", label: "Celsius (°C)" },
      { value: "f", label: "Fahrenheit (°F)" },
    ]
  }
};

export const UnitConverter = () => {
  // Currency state
  const [currencyAmount, setCurrencyAmount] = useState("100");
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("EUR");
  const [currencyResult, setCurrencyResult] = useState(null);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  // Unit state
  const [unitCategory, setUnitCategory] = useState("length");
  const [unitValue, setUnitValue] = useState("1");
  const [fromUnit, setFromUnit] = useState("m");
  const [toUnit, setToUnit] = useState("ft");
  const [unitResult, setUnitResult] = useState(null);
  const [unitLoading, setUnitLoading] = useState(false);

  // Update units when category changes
  useEffect(() => {
    const units = unitCategories[unitCategory].units;
    if (units.length >= 2) {
      setFromUnit(units[0].value);
      setToUnit(units[1].value);
      setUnitResult(null);
    }
  }, [unitCategory]);

  const convertCurrency = async () => {
    if (!currencyAmount) {
      toast.error("Please enter an amount");
      return;
    }

    setCurrencyLoading(true);
    try {
      const response = await axios.post(`${API}/convert/currency`, {
        amount: parseFloat(currencyAmount),
        from_currency: fromCurrency,
        to_currency: toCurrency
      });
      setCurrencyResult(response.data);
    } catch (error) {
      toast.error("Conversion failed");
      console.error(error);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const convertUnit = async () => {
    if (!unitValue) {
      toast.error("Please enter a value");
      return;
    }

    setUnitLoading(true);
    try {
      const response = await axios.post(`${API}/convert/units`, {
        value: parseFloat(unitValue),
        from_unit: fromUnit,
        to_unit: toUnit
      });
      setUnitResult(response.data);
    } catch (error) {
      toast.error("Conversion failed");
      console.error(error);
    } finally {
      setUnitLoading(false);
    }
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setCurrencyResult(null);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setUnitResult(null);
  };

  const currentUnits = unitCategories[unitCategory].units;

  return (
    <Layout 
      title="Unit Converter" 
      subtitle="Currency and unit conversions for international planners"
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <Tabs defaultValue="currency" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-secondary">
            <TabsTrigger value="currency" className="data-[state=active]:bg-purple-600">
              <DollarSign className="h-4 w-4 mr-2" />
              Currency
            </TabsTrigger>
            <TabsTrigger value="units" className="data-[state=active]:bg-purple-600">
              <Ruler className="h-4 w-4 mr-2" />
              Units
            </TabsTrigger>
          </TabsList>

          {/* Currency Tab */}
          <TabsContent value="currency" className="space-y-4">
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg uppercase">
                  <DollarSign className="h-5 w-5 text-purple-400" />
                  Currency Converter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-5 gap-3 items-end">
                  <div className="col-span-2 space-y-2">
                    <Label className="label-tactical">From</Label>
                    <Select value={fromCurrency} onValueChange={setFromCurrency}>
                      <SelectTrigger className="tactical-input" data-testid="from-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-center">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={swapCurrencies}
                      data-testid="swap-currencies-button"
                    >
                      <ArrowRightLeft className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label className="label-tactical">To</Label>
                    <Select value={toCurrency} onValueChange={setToCurrency}>
                      <SelectTrigger className="tactical-input" data-testid="to-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map((curr) => (
                          <SelectItem key={curr} value={curr}>{curr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-tactical">Amount</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={currencyAmount}
                    onChange={(e) => setCurrencyAmount(e.target.value)}
                    className="tactical-input text-2xl text-center"
                    data-testid="currency-amount-input"
                  />
                </div>

                <Button 
                  onClick={convertCurrency} 
                  className="tactical-btn w-full bg-purple-600 hover:bg-purple-700"
                  disabled={currencyLoading}
                  data-testid="convert-currency-button"
                >
                  {currencyLoading ? "Converting..." : "Convert"}
                </Button>

                {currencyResult && (
                  <div className="p-6 bg-purple-500/10 border border-purple-500/30 rounded-sm text-center">
                    <div className="flex items-center justify-center gap-4">
                      <div>
                        <p className="text-2xl font-mono font-bold">
                          {currencyResult.from_amount.toLocaleString()} {currencyResult.from_currency}
                        </p>
                      </div>
                      <ArrowRight className="h-6 w-6 text-purple-400" />
                      <div>
                        <p className="text-3xl font-mono font-bold text-purple-400" data-testid="currency-result">
                          {currencyResult.to_amount.toLocaleString()} {currencyResult.to_currency}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Rate: 1 {currencyResult.from_currency} = {currencyResult.rate} {currencyResult.to_currency}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Units Tab */}
          <TabsContent value="units" className="space-y-4">
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg uppercase">
                  <Ruler className="h-5 w-5 text-purple-400" />
                  Unit Converter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Category Selection */}
                <div className="flex flex-wrap gap-2">
                  {Object.entries(unitCategories).map(([key, cat]) => (
                    <Button
                      key={key}
                      variant={unitCategory === key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setUnitCategory(key)}
                      className={unitCategory === key ? "bg-purple-600 hover:bg-purple-700" : ""}
                      data-testid={`category-${key}-button`}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </div>

                <div className="grid grid-cols-5 gap-3 items-end">
                  <div className="col-span-2 space-y-2">
                    <Label className="label-tactical">From</Label>
                    <Select value={fromUnit} onValueChange={setFromUnit}>
                      <SelectTrigger className="tactical-input" data-testid="from-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-center">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={swapUnits}
                      data-testid="swap-units-button"
                    >
                      <ArrowRightLeft className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label className="label-tactical">To</Label>
                    <Select value={toUnit} onValueChange={setToUnit}>
                      <SelectTrigger className="tactical-input" data-testid="to-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentUnits.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="label-tactical">Value</Label>
                  <Input
                    type="number"
                    placeholder="1"
                    value={unitValue}
                    onChange={(e) => setUnitValue(e.target.value)}
                    className="tactical-input text-2xl text-center"
                    data-testid="unit-value-input"
                  />
                </div>

                <Button 
                  onClick={convertUnit} 
                  className="tactical-btn w-full bg-purple-600 hover:bg-purple-700"
                  disabled={unitLoading}
                  data-testid="convert-unit-button"
                >
                  {unitLoading ? "Converting..." : "Convert"}
                </Button>

                {unitResult && (
                  <div className="p-6 bg-purple-500/10 border border-purple-500/30 rounded-sm text-center">
                    <div className="flex items-center justify-center gap-4">
                      <div>
                        <p className="text-2xl font-mono font-bold">
                          {unitResult.from_value} {unitResult.from_unit}
                        </p>
                      </div>
                      <ArrowRight className="h-6 w-6 text-purple-400" />
                      <div>
                        <p className="text-3xl font-mono font-bold text-purple-400" data-testid="unit-result">
                          {unitResult.to_value} {unitResult.to_unit}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Reference */}
            <Card className="tactical-card">
              <CardHeader>
                <CardTitle className="text-lg uppercase">Quick Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <p className="font-semibold text-purple-400">Length</p>
                    <p className="text-muted-foreground">1 m = 3.28 ft</p>
                    <p className="text-muted-foreground">1 ft = 0.305 m</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-semibold text-purple-400">Area</p>
                    <p className="text-muted-foreground">1 m² = 10.76 ft²</p>
                    <p className="text-muted-foreground">1 ft² = 0.093 m²</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-semibold text-purple-400">Weight</p>
                    <p className="text-muted-foreground">1 kg = 2.2 lb</p>
                    <p className="text-muted-foreground">1 lb = 0.45 kg</p>
                  </div>
                  <div className="space-y-1">
                    <p className="font-semibold text-purple-400">Volume</p>
                    <p className="text-muted-foreground">1 L = 0.26 gal</p>
                    <p className="text-muted-foreground">1 gal = 3.79 L</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default UnitConverter;
